import {
  addMonths,
  differenceInCalendarMonths,
  isSameMonth,
  startOfMonth,
} from 'date-fns';

import { clampMonth, isWithinBounds } from './calendarPeriods';
import type { CalendarPeriodBounds } from './calendarPeriods';

export type MonthView = {
  panel: number;
  month: Date;
  isPinned: boolean;
};

export const getPanelBounds = (
  { fromMonth, toMonth }: CalendarPeriodBounds,
  index: number,
  count: number
): CalendarPeriodBounds => ({
  fromMonth: fromMonth && addMonths(fromMonth, index),
  toMonth: toMonth && addMonths(toMonth, index - count + 1),
});

export const shiftMonthViews = (views: MonthView[], months: number) =>
  views.map((view) => ({ ...view, month: addMonths(view.month, months) }));

const withinBoundsOrNull = (views: MonthView[], bounds: CalendarPeriodBounds) =>
  views.every((view) => isWithinBounds(view.month, bounds)) ? views : null;

export const shiftWithinBounds = (
  views: MonthView[],
  months: number,
  bounds: CalendarPeriodBounds
) => withinBoundsOrNull(shiftMonthViews(views, months), bounds);

export const getAdjacentMonthViews = (
  month: Date,
  count: number,
  bounds: CalendarPeriodBounds
): MonthView[] => {
  const firstMonth =
    count > 1
      ? clampMonth(month, getPanelBounds(bounds, 0, count))
      : startOfMonth(month);
  return Array.from({ length: count }, (_, panel) => ({
    panel,
    month: addMonths(firstMonth, panel),
    isPinned: false,
  }));
};

export const isMonthVisible = (views: MonthView[], month: Date) =>
  views.some((view) => isSameMonth(view.month, month));

export const setMonthView = (
  views: MonthView[],
  index: number,
  month: Date
): MonthView[] => {
  const target = startOfMonth(month);

  return views.map((view, viewIndex) => {
    const offset = viewIndex - index;
    if (offset === 0) {
      return { ...view, month: target, isPinned: viewIndex > 0 };
    }

    const adjacent = addMonths(target, offset);
    const keepsOrder =
      offset < 0
        ? differenceInCalendarMonths(view.month, adjacent) <= 0
        : view.isPinned &&
          differenceInCalendarMonths(view.month, adjacent) >= 0;
    return keepsOrder ? view : { ...view, month: adjacent, isPinned: false };
  });
};

export const moveMonthViewWithinBounds = (
  views: MonthView[],
  index: number,
  month: Date,
  bounds: CalendarPeriodBounds
) => withinBoundsOrNull(setMonthView(views, index, month), bounds);
